/**
 * *Despatch together…*'s drops table, headless (V1 completion R5): the shape the request body holds a drop in, what the
 * dry run hints beside it (the place and kits of each drop), and the tick-and-untick the web shell's widget does while a
 * person chooses which drops have gone. Orders decides every rule (which drops may go, the carrier and tracking rules,
 * what a repeat is); this only reads the hints and keeps the body consistent while a person edits it.
 */
import type { FieldHint } from "./object-ui/contract";

/** One drop that has gone, as the request body carries it (`KitDropDespatchRequest`). */
export interface DespatchDropBody {
  /** The drop's number on the kit line, from 1, as the Kits tab and the kit manifest number it. */
  drop: number;
  tracking_number?: string | null;
  carrier?: string | null;
  carrier_name?: string | null;
}

/** The dry run's hint for one drop sent: where it goes and how many kits. */
export interface DropHint {
  drop: number;
  place: string | null;
  postcode: string | null;
  kits: number | null;
}

/** What the dry run hints about the whole table: the kit line, its drops and the rows it was asked about. */
export interface DespatchContext {
  /** ".08 Kits, Autumn". */
  line: string;
  dropCount: number;
  waitingCount: number;
  rows: readonly DropHint[];
}

const EMPTY: DespatchContext = {
  line: "",
  dropCount: 0,
  waitingCount: 0,
  rows: [],
};

/** The drops table's hints, read off the dry run's `field_hints`. */
export function despatchContext(
  hints: Record<string, FieldHint>,
): DespatchContext {
  const context = hints.drops?.widget_context as
    | {
        line?: string;
        drop_count?: number;
        waiting_count?: number;
        rows?: DropHint[];
      }
    | null
    | undefined;
  if (!context) return EMPTY;
  return {
    line: context.line ?? "",
    dropCount: context.drop_count ?? 0,
    waitingCount: context.waiting_count ?? 0,
    rows: context.rows ?? [],
  };
}

/** How a drop reads in the table: its place, else "Drop 14" before the dry run has said. */
export function dropName(drop: number, hint: DropHint | undefined): string {
  return hint?.place?.trim() || `Drop ${drop}`;
}

/** The kits the drops hold in all (those the dry run has hinted). */
export function kitsIn(
  drops: readonly DespatchDropBody[],
  hints: ReadonlyMap<number, DropHint>,
): number {
  return drops.reduce((sum, d) => sum + (hints.get(d.drop)?.kits ?? 0), 0);
}

/**
 * The ticked drops after drop ``drop`` is ticked or unticked. A ticked drop keeps what the person typed against it
 * (its tracking number, its carrier); unticking sets it aside and ticking it again brings those back. The list stays
 * in drop order so the rows read as the manifest does.
 */
export function withDropTicked(
  ticked: readonly DespatchDropBody[],
  setAside: ReadonlyMap<number, DespatchDropBody>,
  drop: number,
  isTicked: boolean,
): { ticked: DespatchDropBody[]; setAside: Map<number, DespatchDropBody> } {
  const aside = new Map(setAside);
  if (!isTicked) {
    const own = ticked.find((d) => d.drop === drop);
    if (own) aside.set(drop, own);
    return { ticked: ticked.filter((d) => d.drop !== drop), setAside: aside };
  }
  if (ticked.some((d) => d.drop === drop))
    return { ticked: [...ticked], setAside: aside };
  const back = aside.get(drop) ?? { drop };
  aside.delete(drop);
  return {
    ticked: [...ticked, back].sort((a, b) => a.drop - b.drop),
    setAside: aside,
  };
}

/** One drop's tracking number typed in (empty clears it). */
export function withTracking(
  drops: readonly DespatchDropBody[],
  drop: number,
  text: string,
): DespatchDropBody[] {
  const tracking = text.trim() === "" ? null : text;
  return drops.map((d) =>
    d.drop === drop ? { ...d, tracking_number: tracking } : d,
  );
}
